import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import FlightIcon from "@mui/icons-material/Flight";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { usePaginationParams } from "../hooks/usePaginationParams";
import { useMe } from "../hooks/useMe";
import Sidebar from "../components/Sidebar";
import API from "../APIs/API";
import { makeQueryString } from "../APIs/request";
import "../styles/list.scss";

// ─── TAB IDs ────────────────────────────────────────────────────────────────
const TAB_APPLY  = "apply";
const TAB_RESUME = "resume";

// ─── STATUS BADGE ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending:  { bg: "#fef9c3", color: "#92400e", label: "Pending"  },
    approved: { bg: "#dcfce7", color: "#166534", label: "Approved" },
    declined: { bg: "#fee2e2", color: "#991b1b", label: "Declined" },
  };
  const s = map[status?.toLowerCase()] ?? map.pending;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 10, backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

// ─── COMPONENT ──────────────────────────────────────────────────────────────
const DutyTravel = ({ type }) => {
  const { admin_location_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions, user: loggedUser } = useMe();
  const { gridParams, queryParams } = usePaginationParams();
  const [activeTab, setActiveTab] = useState(TAB_APPLY);

  const statusMutation = useMutation(
    ({ id, status }) => new API().updateDutyTravelStatus(id, { status }),
    {
      onSuccess: (d) => {
        if (d) { queryClient.invalidateQueries(["duty_travel"]); toast.success("Status updated."); }
        else toast.error("Failed to update status.");
      },
      onError: () => toast.error("Failed to update status."),
    }
  );

  const pageUserType = type === "Teacher" ? "teachers" : "non-teachers";

  // ── Fetch duty travel records ────────────────────────────────────────────
  const { data: listData = {}, isFetching } = useQuery(
    ["duty_travel", admin_location_id, activeTab, JSON.stringify(queryParams)],
    async () => {
      const qs = makeQueryString({ ...queryParams, admin_location_id });
      const res = await new API().getDutyTravels(qs);
      if (!res) return { data: [], total: 0 };
      // filter by tab: apply = all, resume = approved only
      const rows = activeTab === TAB_RESUME
        ? (res.data ?? []).filter(r => r.status === "approved")
        : (res.data ?? []);
      return { rows, count: res.total ?? rows.length };
    },
    { keepPreviousData: true }
  );

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="cellAction">
          <div
            className="viewButton"
            onClick={() => navigate(`/${pageUserType}/${admin_location_id}/duty-travel/${row.id}`)}
          >
            View
          </div>
        </div>
      ),
    },
    { field: "employee_no",       headerName: "Employee No",       width: 130 },
    {
      field: "name_report",
      headerName: "Name Report",
      width: 170,
      renderCell: ({ row }) => row.name_report ?? row.name ?? "",
    },
    { field: "occup_pos_title",   headerName: "Occup Pos Title",   width: 180 },
    {
      field: "start_date",
      headerName: "Departure Date & Time",
      width: 180,
      renderCell: ({ row }) => row.start_date ? dayjs(row.start_date).format("DD/MM/YYYY HH:mm") : "",
    },
    {
      field: "end_date",
      headerName: "Arrival Date & Time",
      width: 180,
      renderCell: ({ row }) => row.end_date ? dayjs(row.end_date).format("DD/MM/YYYY HH:mm") : "",
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <select
          className="form-select form-select-sm"
          style={{ fontSize: 12, padding: "2px 24px 2px 8px" }}
          value={row.status === "approved" ? "approved" : row.status === "declined" ? "declined" : ""}
          onChange={(e) => {
            if (e.target.value) statusMutation.mutate({ id: row.id, status: e.target.value });
          }}
        >
          <option value="">— Pending —</option>
          <option value="approved">Approved</option>
          <option value="declined">Disapproved</option>
        </select>
      ),
    },
  ];

  // ── Tab style helper ─────────────────────────────────────────────────────
  const tabStyle = (id) => ({
    padding: "8px 20px",
    cursor: "pointer",
    fontWeight: activeTab === id ? 700 : 400,
    color: activeTab === id ? "#7c3aed" : "#374151",
    borderBottom: activeTab === id ? "3px solid #7c3aed" : "3px solid transparent",
    background: "none",
    border: "none",
    fontSize: 14,
  });

  return (
    <div className="wrapper">
      <div className="sidebar"><Sidebar /></div>
      <div className="content">
        <div className="list">
          <div className="listContainer">
            <div className="datatable">

              {/* Back */}
              <Link to={`/${pageUserType}/${admin_location_id}`} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br /><br />

              {/* Title */}
              <div className="datatableTitle">
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FlightIcon style={{ color: "#7c3aed" }} />
                  Duty Travel
                </span>
              </div>

              {/* Tabs */}
              <div style={{ borderBottom: "1px solid #e5e7eb", marginBottom: 16, display: "flex", gap: 4 }}>
                <button style={tabStyle(TAB_APPLY)}  onClick={() => setActiveTab(TAB_APPLY)}>
                  Apply for Duty Travel
                </button>
                <button style={tabStyle(TAB_RESUME)} onClick={() => setActiveTab(TAB_RESUME)}>
                  Resume Work from Duty Travel
                </button>
              </div>

              {/* Apply Tab — list of duty travel requests */}
              {activeTab === TAB_APPLY && (
                <>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                    {/* TODO: show "Apply" button only for eligible employees */}
                    <button
                      className="addBtn"
                      onClick={() => navigate(`/${pageUserType}/${admin_location_id}/duty-travel/apply`)}
                    >
                      + Apply for Duty Travel
                    </button>
                  </div>

                  <DataGrid
                    className="datagrid"
                    rows={listData?.rows ?? []}
                    columns={columns}
                    rowCount={listData?.count ?? 0}
                    loading={isFetching}
                    getRowHeight={() => "auto"}
                    sx={{ "& .MuiDataGrid-cell": { paddingTop: "10px", paddingBottom: "10px" } }}
                    {...gridParams}
                  />
                </>
              )}

              {/* Resume Tab — list of trips pending acquittal */}
              {activeTab === TAB_RESUME && (
                <>
                  <div style={{ marginBottom: 12, color: "#6b7280", fontSize: 13 }}>
                    {/* TODO: show trips where employee has returned and acquittal is pending */}
                    Trips awaiting acquittal will appear here.
                  </div>

                  <DataGrid
                    className="datagrid"
                    rows={listData?.rows ?? []}
                    columns={columns}
                    rowCount={listData?.count ?? 0}
                    loading={isFetching}
                    getRowHeight={() => "auto"}
                    sx={{ "& .MuiDataGrid-cell": { paddingTop: "10px", paddingBottom: "10px" } }}
                    {...gridParams}
                  />
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DutyTravel;
